import React, { useCallback, useState, useEffect } from 'react';
import { Checkbox } from '@moda/om';
import {
  UnsubscribePageQueryNotificationPreference,
  UnsubscribePageQueryNotificationPreferencesEntityFlags
} from './unsubscribePage.rest';
import './UnsubscribePageSetting.scss';

type UnsubscribePageSettingProps = {
  setting: UnsubscribePageQueryNotificationPreference;
  onChange?(id: string, value: number): void;
  onChangeEntitiesToRemove?(entities: UnsubscribePageQueryNotificationPreferencesEntityFlags): void;
};

const BINARY_FALSE_VALUE = 0;
const BINARY_TRUE_VALUE = 1;

export const UnsubscribePageSetting: React.FC<UnsubscribePageSettingProps> = ({
  setting,
  onChange,
  onChangeEntitiesToRemove
}) => {
  const {
    id,
    attributes: {
      frequency: { name, value: initialValue }
    }
  } = setting;

  const [checked, setChecked] = useState(name !== 'disabled');
  const [value, setValue] = useState(initialValue);
  const [entitiesToRemove, setEntitiesToRemove] =
    useState<UnsubscribePageQueryNotificationPreferencesEntityFlags>({});

  const handleBinaryChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
    setValue(event.target.checked ? BINARY_TRUE_VALUE : BINARY_FALSE_VALUE);
  }, []);

  const handleEntityChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const id = event.target.name;
      const entityId = parseInt(event.target.value, 10);
      const entity = setting.attributes.entities.find(entity => entity.id === entityId);

      if (!entity) return;

      setEntitiesToRemove(prevEntities => {
        // If the entity is checked then we remove the entity from the array associated with the flag ID
        if (event.target.checked) {
          if (!prevEntities) return {};

          // Pull off the relevant flag and its entities
          const { [id]: filterableEntities, ...remainingEntities } = prevEntities;

          // Remove the relevant entity
          const filteredEntities = filterableEntities.filter(entity => entity.id !== entityId);

          // Return back out any other flags, unchanged, in addition to the relevant flag with the
          // relevant entity removed from the array
          return {
            ...remainingEntities,
            ...(filteredEntities.length > 0 ? { [id]: filteredEntities } : {})
          };
        }

        // If the entity is unchecked then we are adding the key if it doesn't exist and mixing in
        // any existing entities associated with that flag
        return {
          [id]: [...((prevEntities && prevEntities[id]) || []), entity]
        };
      });
    },
    [setting.attributes.entities]
  );

  useEffect(() => {
    onChange?.(id, value);
  }, [id, onChange, value]);

  useEffect(() => {
    onChangeEntitiesToRemove?.(entitiesToRemove);
  }, [entitiesToRemove, onChangeEntitiesToRemove]);

  return (
    <div className="UnsubscribePageSetting">
      <Checkbox
        key={setting.id}
        className="UnsubscribePageSetting__checkbox"
        onChange={handleBinaryChange}
        checked={checked}
      >
        <>
          <div className="UnsubscribePageSetting__label">{setting.attributes.options.human}</div>

          {setting.attributes.options.tooltipText && (
            <div className="UnsubscribePageSetting__explanation">
              {setting.attributes.options.tooltipText}
            </div>
          )}
        </>
      </Checkbox>

      {/* Display any associated entities */}
      {checked && setting.attributes.entities.length > 0 && (
        <div className="UnsubscribePageSetting__entities">
          {setting.attributes.entities.map(entity => {
            return (
              <Checkbox
                key={entity.id}
                name={setting.id}
                value={entity.id}
                defaultChecked
                onChange={handleEntityChange}
              >
                {entity.entityName}
              </Checkbox>
            );
          })}
        </div>
      )}
    </div>
  );
};
